import type { TeamLocation } from "@redotech/admin-sdk/rpc/schema/locations/get-team-locations";
import { useHandler } from "@redotech/react-util/hook";
import type { LocationId } from "@redotech/redo-model/ids";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { SettingCard } from "../returns-package-protection/v2/component/setting-card";

export const LocationsCard = memo(function LocationsCard({
  teamId,
}: {
  teamId: string;
}) {
  const queryArgs = useMemo(() => ({ teamId }), [teamId]);
  const locationsQuery = useRpcQuery(
    RedoAdminRpcClientContext,
    "getTeamLocationsForAdmin",
    queryArgs,
  );

  const locations = useMemo(
    () => locationsQuery.data?.locations ?? [],
    [locationsQuery.data],
  );

  const toggleMutation = useRpcMutation(RedoAdminRpcClientContext, {
    method: "toggleLocationReturnsCenter",
  });

  const [pendingChanges, setPendingChanges] = useState<
    Map<LocationId, boolean>
  >(new Map());

  const hasPendingChanges = useMemo(
    () => pendingChanges.size > 0,
    [pendingChanges],
  );

  const handleToggle = useCallback(
    (locationId: LocationId, value: boolean) => {
      const serverValue =
        locations.find((l) => l.id === locationId)?.isReturnsCenter ?? false;
      if (value === serverValue) {
        setPendingChanges((prev) => {
          const next = new Map(prev);
          next.delete(locationId);
          return next;
        });
      } else {
        setPendingChanges((prev) => new Map(prev).set(locationId, value));
      }
    },
    [locations],
  );

  const handleSave = useHandler(async () => {
    try {
      await Promise.all(
        [...pendingChanges.entries()].map(([locationId, isReturnsCenter]) =>
          toggleMutation.mutateAsync({ teamId, locationId, isReturnsCenter }),
        ),
      );
      setPendingChanges(new Map());
      void locationsQuery.refetch();
      toast("Location changes saved", { variant: "success" });
    } catch {
      toast("Failed to save location changes", { variant: "error" });
    }
  });

  const handleDiscard = useCallback(() => {
    setPendingChanges(new Map());
  }, []);

  if (locationsQuery.isPending && locations.length === 0) {
    return (
      <SettingCard>
        <Flex align="center" dir="column" p="xl">
          <Text fontSize="sm" textColor="tertiary">
            Loading locations...
          </Text>
        </Flex>
      </SettingCard>
    );
  }

  if (locationsQuery.isError) {
    return (
      <SettingCard>
        <Flex align="center" dir="column" p="xl">
          <Text fontSize="sm" textColor="error">
            Failed to load locations
          </Text>
        </Flex>
      </SettingCard>
    );
  }

  if (locations.length === 0) {
    return (
      <SettingCard>
        <Flex align="center" dir="column" p="xl">
          <Text fontSize="sm" textColor="tertiary">
            No locations found for this team
          </Text>
        </Flex>
      </SettingCard>
    );
  }

  return (
    <SettingCard>
      <Flex align="stretch" dir="column" gap="none">
        {locations.map((location) => (
          <LocationRow
            key={location.id}
            location={location}
            onToggle={handleToggle}
            pendingValue={pendingChanges.get(location.id)}
          />
        ))}
      </Flex>
      {hasPendingChanges && (
        <Flex dir="row" gap="sm" justify="flex-end" p="lg">
          <RedoButton
            hierarchy="secondary"
            onClick={handleDiscard}
            size="xs"
            text="Discard"
          />
          <RedoButton
            hierarchy="primary"
            onClick={handleSave}
            pending={toggleMutation.isPending}
            size="xs"
            text="Save"
          />
        </Flex>
      )}
    </SettingCard>
  );
});

const LocationRow = memo(function LocationRow({
  location,
  onToggle,
  pendingValue,
}: {
  location: TeamLocation;
  onToggle: (locationId: LocationId, value: boolean) => void;
  pendingValue: boolean | undefined;
}) {
  const addressParts = [location.city, location.state, location.country].filter(
    Boolean,
  );
  const addressSummary = addressParts.join(", ");

  const currentValue = pendingValue ?? !!location.isReturnsCenter;

  const handleSetValue = useCallback(
    (value: boolean) => {
      onToggle(location.id, value);
    },
    [onToggle, location.id],
  );

  return (
    <Flex align="center" dir="row" gap="lg" justify="space-between" p="lg">
      <Flex dir="column" gap="xs" grow="1">
        <Flex align="center" dir="row" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            {location.name}
          </Text>
          {location.isActive === false && (
            <Text fontSize="xs" textColor="tertiary">
              (inactive)
            </Text>
          )}
        </Flex>
        {addressSummary && (
          <Text fontSize="xs" textColor="tertiary">
            {addressSummary}
          </Text>
        )}
      </Flex>
      <RedoToggle
        label="Returns center"
        setValue={handleSetValue}
        size="sm"
        value={currentValue}
      />
    </Flex>
  );
});
